import { createPortal } from "react-dom";
import { X, AlertTriangle, Check, Lock } from "lucide-react";
import { verifyDay } from "@/lib/verify";
import { initials } from "@/lib/scoring";
import { CardDiscrepancies } from "@/screens/CardDiscrepancies";

export default function ScoreReviewModal({ open, onClose, event, day, updateScores, onFinalizeAnyway, canFinalize }) {
  if (!open || !day) return null;
  const report = verifyDay(day);
  const nameOf = (pid) => event.players.find((p) => p.id === pid)?.name || "?";

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="score-review-modal">
      <div className="tga-card w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="font-display text-2xl">Scorecard review</div>
            <div className="text-xs text-[color:var(--brass-dark)]">{day.label} · every player's card must match before finalising</div>
          </div>
          <button data-testid="score-review-close" className="ghost-btn rounded-lg p-2" onClick={onClose}><X size={16} /></button>
        </div>

        {report.ok ? (
          <div className="border border-emerald-700/30 bg-emerald-50 rounded-lg p-3 text-sm inline-flex items-center gap-2 w-full" data-testid="score-review-ok">
            <Check size={16} className="text-emerald-700" /> All cards complete and matching.
          </div>
        ) : (
          <div className="border border-amber-600/40 bg-amber-50 rounded-lg p-3 text-sm flex items-start gap-2" data-testid="score-review-summary">
            <AlertTriangle size={16} className="text-amber-700 shrink-0 mt-0.5" />
            <div>
              {report.missingCount > 0 && <div><strong>{report.missingCount}</strong> card{report.missingCount === 1 ? "" : "s"} incomplete.</div>}
              {report.discrepancyCount > 0 && <div><strong>{report.discrepancyCount}</strong> hole{report.discrepancyCount === 1 ? "" : "s"} where cards disagree — tap the correct score to resolve.</div>}
            </div>
          </div>
        )}

        <div className="space-y-4 mt-4">
          {report.groups.map(({ gi, missing, discrepancies }) => (
            <div key={gi} className="border border-[rgba(27,51,34,0.15)] rounded-xl p-3" data-testid={`score-review-group-${gi}`}>
              <div className="text-xs uppercase text-[color:var(--brass-dark)] mb-2">Group {String.fromCharCode(65 + gi)}</div>
              {missing.length === 0 && discrepancies.length === 0 && <div className="text-sm opacity-70">Cards complete and matching.</div>}
              {missing.length > 0 && (
                <div className="mb-3">
                  <div className="text-xs font-medium mb-1">Incomplete cards</div>
                  <div className="flex flex-wrap gap-2">
                    {missing.map((m) => (
                      <span key={m.scorerId} data-testid={`score-review-missing-${m.scorerId}`} className="inline-flex items-center gap-1 text-xs rounded-full border border-amber-600/40 bg-amber-50 px-2 py-1">
                        <span className="avatar" style={{ width: 20, height: 20, fontSize: 10 }}>{initials(nameOf(m.scorerId))}</span>
                        {nameOf(m.scorerId)} · {m.started ? `${m.holesMissing} hole${m.holesMissing === 1 ? "" : "s"} missing` : "not started"}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {discrepancies.length > 0 && (
                <CardDiscrepancies event={event} day={day} gi={gi} discrepancies={discrepancies} updateScores={updateScores} />
              )}
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mt-5">
          {report.ok ? (
            <button data-testid="score-review-finalize" className="brass-btn rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-1 flex-1" onClick={onFinalizeAnyway}><Lock size={14} /> Finalise {day.label}</button>
          ) : canFinalize ? (
            <button data-testid="score-review-finalize-anyway" className="brass-btn rounded-lg px-4 py-2.5 text-sm inline-flex items-center justify-center gap-1 flex-1" onClick={onFinalizeAnyway}><Lock size={14} /> Finalise anyway (use current scores)</button>
          ) : (
            <div className="text-xs text-[color:var(--text-dark)]/70 flex-1 self-center" data-testid="score-review-organizer-only">Only the trip organiser can finalise while cards are incomplete or disagree.</div>
          )}
          <button data-testid="score-review-back" className="ghost-btn rounded-lg px-4 py-2.5 text-sm" onClick={onClose}>Back to scoring</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
